import { Divider, Grid, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import get from 'lodash/get';
import { useMemo, useState } from 'react';
import MetricPanel, { MetricPanelDelta } from './MetricPanel';
import {
  CallMetricsResponse,
  MetricPanelProps,
  MetricsField,
} from './MetricTypes';

const useStyles = makeStyles((theme: Theme) => ({
  title: {
    fill: '#333333',
    fontSize: '16px',
    fontWeight: 600,
    marginLeft: '84px',
    '.hover &': {
      color: theme.palette.mode === 'dark' ? '#0098C7 !important' : '#333333',
    },
  },
  titleValue: {
    color: theme.palette.mode === 'dark' ? '#ffffff !important' : '#333333',
    fontSize: '48px',
    fontWeight: 600,
    cursor: 'pointer',
    marginLeft: '84px',
    '.hover &': {
      color: theme.palette.mode === 'dark' ? '#0098C7 !important' : '#333333',
    },
  },
  callMetricsPanelContainerOutline: {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignContent: 'space-between',
    margin: '0 84px 42px',
  },
  callMetricsEmptyText: {
    fontSize: '14px',
  },
}));

const HeroMetric = (props: {
  loading: boolean;
  metricsDisplayArray: MetricPanelProps[];
  callMetricFields: MetricsField[];
  metricOverview?: MetricPanelProps;
}) => {
  const {
    loading,
    metricsDisplayArray,
    callMetricFields,
    metricOverview,
  } = props;
  const classes = useStyles();
  const [isHover, setIsHover] = useState(false);
  if (loading) {
    const callMetricFieldsDisplayArray = callMetricFields
      .filter(
        (metricField: MetricsField) =>
          metricField?.show && metricField?.order !== undefined
      )
      .sort(
        (a: MetricsField, b: MetricsField) => (a?.order ?? 0) - (b?.order ?? 0)
      );
    return (
      <div className={classes.callMetricsPanelContainerOutline}>
        {callMetricFieldsDisplayArray?.length
          ? callMetricFieldsDisplayArray.map(
              (metricField: MetricsField, index: number) => {
                return (
                  <MetricPanel
                    key={index}
                    title={''}
                    data={''}
                    percentage={''}
                    previous={''}
                    loading={loading}
                    metricField={metricField}
                  />
                );
              }
            )
          : null}
      </div>
    );
  }

  return (
    <>
      <Grid
        container
        spacing={3}
        onMouseEnter={() => setIsHover(true)}
        onMouseLeave={() => setIsHover(false)}
        className={isHover ? 'hover' : ''}
      >
        <Grid
          direction="column"
          item
          xs={12}
          container
          justifyContent="flex-start"
        >
          <div
            style={{
              textAlign: 'left',
              justifyContent: 'start',
              maxWidth: '450px',
              padding: 15,
            }}
          >
            <div className={classes.title}>{metricOverview?.title}</div>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                cursor: 'pointer',
                maxWidth: '450px',
              }}
            >
              <div
                className={classes.titleValue}
                style={{ color: isHover ? '#0098C7' : '#333333' }}
              >
                {metricOverview?.data}
              </div>
              <div>
                <MetricPanelDelta
                  percentage={metricOverview?.percentage ?? 0}
                  previous={metricOverview?.previous}
                />
              </div>
            </div>
          </div>
        </Grid>
        <Grid
          container
          item
          xs={12}
          spacing={3}
          justifyContent="flex-start"
          style={{ marginLeft: '84px', marginRight: '84px' }}
        >
          {metricsDisplayArray?.length ? (
            metricsDisplayArray.map(
              (metricToDisplay: MetricPanelProps, index: number) => {
                return (
                  <div key={index}>
                    <Grid>
                      <MetricPanel
                        key={index}
                        title={metricToDisplay.title}
                        data={metricToDisplay.data}
                        percentage={metricToDisplay.percentage}
                        previous={metricToDisplay.previous}
                        loading={metricToDisplay.loading}
                        metricField={metricToDisplay.metricField}
                      />
                    </Grid>
                    {metricOverview?.addEmptyGridItem &&
                      (index + 1) % metricOverview?.addEmptyGridItem === 0 && (
                        <Grid item xs={12} style={{ padding: 0 }} />
                      )}
                  </div>
                );
              }
            )
          ) : (
            <p className={classes.callMetricsEmptyText}>
              No Call Metrics to Display
            </p>
          )}
        </Grid>
      </Grid>
      {metricOverview?.title !== 'Time Saved (min:sec)' && (
        <Divider style={{ marginBottom: '42px', marginTop: '42px' }} />
      )}
    </>
  );
};

const CallMetrics = (props: {
  callMetrics: CallMetricsResponse | undefined;
  previousRange: string;
  loading: boolean;
  hasCallTranscriptionProduct: boolean;
}) => {
  const { callMetrics, loading, previousRange } = props;

  const totalCallsTranscribedFields: MetricsField[] = [
    {
      name: 'transcriptions.score',
      displayName: 'Average Call Score',
      show: true,
      order: 0,
      useAverage: true,
    },
    {
      name: 'transcriptions.percentPhrasesMatched',
      displayName: 'Average % of Phrases Matched',
      show: true,
      order: 1,
      useAverage: true,
    },
    {
      name: 'transcriptions.percentKeywordsMatched',
      displayName: 'Average % of Keywords Matched',
      show: true,
      order: 2,
      useAverage: true,
    },
    {
      name: 'transcriptions.positiveSentiment',
      displayName: '% of Positive Sentiment Calls',
      show: true,
      order: 3,
      useAverage: true,
      format: (value) => (typeof value === 'number' ? `${value}% Pos.` : ''),
      dataFontSize: 22,
    },
    {
      name: 'transcriptions.neutralSentiment',
      displayName: '% of Neutral Sentiment Calls',
      show: true,
      order: 4,
      useAverage: true,
      format: (value) => (typeof value === 'number' ? `${value}% Neu.` : ''),
      dataFontSize: 22,
    },
    {
      name: 'transcriptions.negativeSentiment',
      displayName: '% of Negative Sentiment Calls',
      show: true,
      order: 5,
      useAverage: true,
      format: (value) => (typeof value === 'number' ? `${value}% Neg.` : ''),
      dataFontSize: 22,
    },
  ];

  const totalCallsHandledFields: MetricsField[] = [
    {
      name: 'calls.outbound',
      displayName: 'Total Outbound Calls',
      show: true,
      order: 0,
    },
    {
      name: 'connections.outbound',
      displayName: 'Total Outbound Connections',
      show: true,
      order: 1,
    },
    {
      name: 'calls.inbound.missed',
      displayName: 'Total Missed Inbound Calls',
      show: true,
      order: 4,
    },
    {
      name: 'calls.inbound',
      displayName: 'Total Inbound Calls',
      show: true,
      order: 2,
    },
    {
      name: 'connections.inbound',
      displayName: 'Total Inbound Connections',
      show: true,
      order: 3,
    },
    {
      name: 'calls.inbound.missedPercent',
      displayName: '% Of Missed Calls',
      show: true,
      order: 5,
      useAverage: false,
    },
  ];

  const AverageHandleTimeFields: MetricsField[] = [
    {
      name: 'calls.answerSpeed',
      displayName: 'Average Speed to Answer',
      show: true,
      order: 0,
      useAverage: true,
      isMilliseconds: true,
    },
    {
      name: 'calls.callLength',
      displayName: 'Average Call Length',
      show: true,
      order: 1,
      useAverage: true,
      isMilliseconds: true,
    },
    {
      name: 'wfm.submitTime',
      displayName: 'Average After Call Work',
      useAverage: true,
      show: true,
      isMilliseconds: true,
      order: 2,
    },
    {
      name: 'wfm.idleTime',
      displayName: 'Average Idle Time',
      useAverage: true,
      show: true,
      isMilliseconds: true,
      order: 3,
    },
    {
      name: 'wfm.dndAverage',
      displayName: 'Average DND Time',
      useAverage: true,
      show: true,
      isMilliseconds: true,
      order: 4,
    },
    {
      name: 'callTime',
      displayName: 'Total Call Time (min:sec)',
      show: true,
      useAverage: false,
      isSeconds: true,
      order: 5,
    },
  ];

  const timeSavedFields: MetricsField[] = [
    {
      name: 'c2c',
      displayName: 'Click to Call',
      show: true,
      order: 0,
      isSeconds: true,
    },
    {
      name: 'incomingCall',
      displayName: 'Incoming Call',
      show: true,
      order: 1,
      isSeconds: true,
    },
    {
      name: 'smsTemplate',
      displayName: 'SMS Template',
      show: true,
      order: 2,
      isSeconds: true,
    },
    {
      name: 'vmDrop',
      displayName: 'VM Drop',
      show: true,
      order: 3,
      isSeconds: true,
    },
    {
      name: 'loggedCall',
      displayName: 'Logged Call',
      show: true,
      order: 4,
      isSeconds: true,
    },
    {
      name: 'autoSMS',
      displayName: 'Auto SMS',
      show: true,
      order: 5,
      isSeconds: true,
    },
  ];
  const getSeconds = (num) => num / 1000;
  const getMins = (num) => num / 60;
  const minutesDecimalFormatter = (
    duration: string | undefined,
    isMill = false
  ): string => {
    const seconds = isMill ? getSeconds(duration) : duration;
    duration = getMins(seconds).toString();
    if (!duration || !Number.parseFloat(duration)) {
      return '0';
    }
    const durationMinutes = Number.parseFloat(duration);
    return `${Math.trunc(
      Number.parseFloat(durationMinutes?.toString() || '0')
    )}:${
      Math.round(
        (Number.parseFloat(durationMinutes?.toString() || '0') % 1) * 60
      ) < 10
        ? '0' +
          Math.round(
            (Number.parseFloat(durationMinutes?.toString() || '0') % 1) * 60
          )
        : Math.round(
            (Number.parseFloat(durationMinutes?.toString() || '0') % 1) * 60
          )
    }`;
  };

  const filterSortMetrics = (metricFields: MetricsField[]) => {
    return metricFields
      .filter(
        (metricField: MetricsField) =>
          metricField?.show && metricField?.order !== undefined
      )
      .sort(
        (a: MetricsField, b: MetricsField) => (a?.order ?? 0) - (b?.order ?? 0)
      );
  };

  const capitalizeFirstLetter = (value: string) =>
    value.charAt(0).toUpperCase() + value.slice(1);

  const formatString = (value: any): string | any =>
    typeof value === 'string' ? capitalizeFirstLetter(value) : value;

  // rounds numbers to 0, 1, or 2 decimal points
  const formatNumber = (value: any): number | any =>
    typeof value === 'number'
      ? Math.round(value * 100) / 100
      : formatString(value);

  const formatData = (metricData, metricField, cb) => {
    const total = metricField.isSeconds
      ? minutesDecimalFormatter(metricData.total)
      : metricField.isMilliseconds
      ? minutesDecimalFormatter(metricData.total, true)
      : formatNumber(metricData.total);
    const average = metricField.isSeconds
      ? minutesDecimalFormatter(metricData.average)
      : metricField.isMilliseconds
      ? minutesDecimalFormatter(metricData.average, true)
      : formatNumber(metricData.average);
    const value = metricField.useAverage ? average : total;
    const data =
      typeof metricField.format === 'function'
        ? metricField.format(value)
        : value;
    const metricToDisplay = {
      title: metricField.displayName,
      data,
      percentage: metricField?.useAverage
        ? metricData.averageDelta
        : metricData.totalDelta,
      previous: previousRange,
      loading,
      metricField,
    };
    cb(metricToDisplay);
  };

  const getMetricsToDisplay = (metricFields: MetricsField[]) => {
    const metricsToDisplay: MetricPanelProps[] = [];
    const fieldsToDisplay = filterSortMetrics(metricFields);
    fieldsToDisplay.forEach((metricField: MetricsField) => {
      const metricData = get(callMetrics, metricField.name, null);
      if (metricData) {
        formatData(metricData, metricField, (metricToDisplay) => {
          metricsToDisplay.push(metricToDisplay);
        });
      }
    });
    return metricsToDisplay;
  };

  const totalCallsTranscribedDisplay = useMemo(() => {
    return getMetricsToDisplay(totalCallsTranscribedFields);
  }, [callMetrics, loading]);

  const handledCallDisplay = useMemo(() => {
    return getMetricsToDisplay(totalCallsHandledFields);
  }, [callMetrics, loading]);

  const averageHandleDisplay = useMemo(() => {
    return getMetricsToDisplay(AverageHandleTimeFields);
  }, [callMetrics, loading]);

  const timeSavedDisplay = useMemo(() => {
    return getMetricsToDisplay(timeSavedFields);
  }, [callMetrics, loading]);

  const heroMetrics = useMemo(() => {
    const savedMetric = get(callMetrics, 'timeSaved', null);
    let timeSavedOverView;
    if (savedMetric) {
      formatData(
        savedMetric,
        {
          name: 'timeSaved',
          displayName: 'Time Saved (min:sec)',
          show: true,
          isSeconds: true,
        },
        (metricToDisplay) => {
          timeSavedOverView = metricToDisplay;
        }
      );
    }
    // Calculate calls handled
    const totalInboundDelta =
      callMetrics?.calls?.inbound?.answered?.totalDelta ?? 0;
    const totalOutboundDelta =
      callMetrics?.calls?.outbound?.answered?.totalDelta ?? 0;
    // Calculate average handle time in seconds
    const averageAnswerSpeedSeconds =
      (callMetrics?.calls?.answerSpeed?.average ?? 0) / 1000;
    const averageCallLengthSeconds =
      (callMetrics?.calls?.callLength?.average ?? 0) / 1000;
    const averageAfterCallWorkSeconds =
      (callMetrics?.wfm?.submitTime?.average ?? 0) / 1000;

    const averageHandleTimeSeconds =
      averageAnswerSpeedSeconds +
      averageCallLengthSeconds +
      averageAfterCallWorkSeconds;

    // Calculate average handle time percentage
    const averageAnswerSpeedPercent =
      callMetrics?.calls?.answerSpeed?.averageDelta ?? 0;
    const averageCallLengthPercent =
      callMetrics?.calls?.callLength?.averageDelta ?? 0;
    const averageAfterCallWorkPercent =
      callMetrics?.wfm?.submitTime?.averageDelta ?? 0;

    const averageHandleTimePercent =
      ((averageAnswerSpeedPercent +
        averageCallLengthPercent +
        averageAfterCallWorkPercent) /
        300) *
      100;

    const metrics = [
      {
        metrics: handledCallDisplay,
        metricOverview: {
          title: 'Total Calls Handled',
          loading: loading,
          previous: previousRange,
          percentage: ((totalInboundDelta + totalOutboundDelta) / 200) * 100,
          data:
            (callMetrics?.calls?.inbound?.answered?.total ?? 0) +
            (callMetrics?.calls?.outbound?.answered?.total ?? 0),
        },
      },
      {
        metrics: averageHandleDisplay,
        metricOverview: {
          title: 'Average Handle Time',
          loading: loading,
          previous: previousRange,
          percentage: String(averageHandleTimePercent),
          data: minutesDecimalFormatter(String(averageHandleTimeSeconds ?? 0)),
        },
      },
      {
        metrics: timeSavedDisplay,
        metricOverview: timeSavedOverView,
      },
    ];

    if (props.hasCallTranscriptionProduct) {
      const transcriptionMetricOverview = {
        title: 'Total Calls Transcribed',
        loading: loading,
        previous: previousRange,
        percentage: callMetrics?.transcriptions?.totalDelta ?? 0,
        data: callMetrics?.transcriptions?.total ?? 0,
        addEmptyGridItem: 3,
      };

      metrics.unshift({
        metrics: totalCallsTranscribedDisplay,
        metricOverview: transcriptionMetricOverview,
      });
    }

    return metrics;
  }, [
    totalCallsTranscribedDisplay,
    handledCallDisplay,
    averageHandleDisplay,
    timeSavedDisplay,
    callMetrics,
    props.hasCallTranscriptionProduct,
  ]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {heroMetrics?.map((el, i) => (
        <HeroMetric
          key={`${[el?.metricOverview?.title, i].join('-')}`}
          loading={loading}
          metricsDisplayArray={el.metrics}
          callMetricFields={totalCallsHandledFields}
          metricOverview={el?.metricOverview}
        />
      ))}
    </div>
  );
};
export default CallMetrics;
